import { authHeader, accountHeader, BASE_URL, handleResponse } from '@helpers/api';
import { ProductEvent } from '@constants/product-events';
import { captureOnCreate } from '@helpers/product-events';

// The backend exposes campaigns + their per-profile child posts under /api/social-posting.
// The frontend retained the `socialPostsService` name post-refactor so call sites elsewhere
// in the app don't have to learn a new symbol — only the endpoints + payload shape changed.
const CAMPAIGNS_URL = `${BASE_URL}api/social-posting/campaigns`;
const POSTS_URL = `${BASE_URL}api/social-posting/posts`;

function get(page, pageSize, filter = {}) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader(),
    };
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('pageSize', pageSize);
    (filter.companies || []).forEach(id => params.append('companies', id));
    (filter.platforms || []).forEach(p => params.append('platforms', p));
    (filter.statuses || []).forEach(s => params.append('statuses', s));
    if (filter.scheduledTimeFrom) params.append('scheduledTimeFrom', filter.scheduledTimeFrom);
    if (filter.scheduledTimeTo) params.append('scheduledTimeTo', filter.scheduledTimeTo);
    if (filter.returnAll) params.append('returnAll', 'true');
    return fetch(`${CAMPAIGNS_URL}?${params.toString()}`, requestOptions).then(handleResponse);
}

function getAvailablePlatforms() {
    const requestOptions = {
        method: 'GET',
        headers: authHeader(),
    };
    return fetch(`${BASE_URL}api/social-posting/available-platforms`, requestOptions).then(handleResponse);
}

function createOrUpdateSocialPost(data) {
    const requestOptions = {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(CAMPAIGNS_URL, requestOptions)
        .then(handleResponse)
        .then(
            captureOnCreate(data, ProductEvent.SocialPostCreated, () => ({
                status: data.status,
                platforms: data.platforms,
                locations: data.companies?.length,
            })),
        );
}

function deleteSocialPost(id) {
    const requestOptions = {
        method: 'DELETE',
        headers: authHeader(),
    };

    return fetch(`${CAMPAIGNS_URL}/${id}`, requestOptions).then(handleResponse);
}

function rescheduleSocialPost(id, scheduledTime) {
    const requestOptions = {
        method: 'PATCH',
        headers: authHeader(),
        body: JSON.stringify({ scheduledTime }),
    };

    return fetch(`${CAMPAIGNS_URL}/${id}/schedule`, requestOptions).then(handleResponse);
}

// The /campaigns/{id}/publish backend route was removed because it had no auth scoping
// and nothing in the UI called it. Publishing is driven by SocialPublishingJob picking
// up due children. If a manual-publish action becomes a real requirement, reintroduce
// the route with CurrentUser-scoped permission checks before adding a helper here.

function retryPost(postId) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
    };

    return fetch(`${POSTS_URL}/${postId}/retry`, requestOptions).then(handleResponse);
}

// Cancels a scheduled post or deletes a published one (removed from the platform). The
// explicit action ('cancel' | 'delete') lets the backend reject with a conflict if the post's
// status changed since the dialog opened, instead of silently performing the other operation.
function cancelOrDeletePost(postId, action) {
    const requestOptions = {
        method: 'DELETE',
        headers: authHeader(),
    };

    return fetch(`${POSTS_URL}/${postId}?action=${action}`, requestOptions).then(handleResponse);
}

function getTikTokCreatorInfo(locationIds, refresh = true) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader(),
    };

    const query = [...locationIds.map(id => `locationIds=${encodeURIComponent(id)}`), `refresh=${refresh}`].join('&');
    return fetch(`${BASE_URL}api/social-posting/tiktok/creator-info?${query}`, requestOptions).then(handleResponse);
}

function uploadSocialPostImages(data) {
    const user = JSON.parse(localStorage.getItem('user'));
    const requestOptions = {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + user.token,
            ...accountHeader(),
        },
        body: data,
    };
    return fetch(`${BASE_URL}api/social-posting/upload-image`, requestOptions).then(handleResponse);
}

// XHR, not fetch: a video upload runs for minutes and fetch exposes no upload progress. The
// result is wrapped in a Response so the shared handleResponse pipeline still applies.
function uploadSocialPostVideos(data, onProgress) {
    const user = JSON.parse(localStorage.getItem('user'));
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BASE_URL}api/social-posting/upload-video`);
        xhr.setRequestHeader('Authorization', 'Bearer ' + user.token);
        Object.entries(accountHeader()).forEach(([name, value]) => xhr.setRequestHeader(name, value));
        if (onProgress) {
            xhr.upload.onprogress = e => {
                if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
            };
        }
        xhr.onload = () => {
            // A throw inside this callback would leave the promise unsettled and wedge the
            // uploader: status is 0 on unload/proxy resets, and null-body statuses reject a body.
            try {
                if (xhr.status < 200 || xhr.status > 599) {
                    reject(new TypeError('Network request failed'));
                    return;
                }
                resolve(new Response(xhr.response || null, { status: xhr.status, statusText: xhr.statusText }));
            } catch (e) {
                reject(e);
            }
        };
        xhr.onerror = () => reject(new TypeError('Network request failed'));
        xhr.onabort = () => reject(new TypeError('Upload aborted'));
        xhr.send(data);
    }).then(handleResponse);
}

export const socialPostsService = {
    get,
    getAvailablePlatforms,
    createOrUpdateSocialPost,
    rescheduleSocialPost,
    deleteSocialPost,
    retryPost,
    cancelOrDeletePost,
    getTikTokCreatorInfo,
    uploadSocialPostImages,
    uploadSocialPostVideos,
};
